/**
 * Pricing Section
 * Использует переиспользуемые компоненты
 * Поддерживает месячные и годовые цены с переключателем
 */
import { useState } from 'react';
import { Text, Icon, Button, Container } from '../components';

export default function PricingSection({ data, styles }) {
  const [isYearly, setIsYearly] = useState(true);

  const plans = data?.plans || [];
  const sectionStyle = data?.style || 'cards';
  const currency = data?.currency || '₽';
  const showPeriodToggle = data?.showPeriodToggle ?? false;
  const monthlyLabel = data?.monthlyLabel || 'Месяц';
  const yearlyLabel = data?.yearlyLabel || 'Год';
  const yearlyDiscount = data?.yearlyDiscount || '';

  // Получить цену в зависимости от выбранного режима
  const getPrice = (plan) => {
    if (isYearly && plan.yearlyPrice) {
      return plan.yearlyPrice;
    }
    return plan.price;
  };

  // Период всегда "месяц" (yearlyPrice - это цена за месяц при оплате за год)
  const getPeriod = (plan) => {
    return plan.period || data?.period || 'месяц';
  };

  // Подпись про оплату за год
  const getBillingNote = (plan) => {
    if (isYearly && plan.yearlyPrice) {
      const total = Number(plan.yearlyPrice) * 12;
      return `при оплате за год (${total.toLocaleString('ru-RU')} ${currency})`;
    }
    return null;
  };

  // Переключатель периода
  const PeriodToggle = () => (
    <div className="flex items-center justify-center gap-3 mb-8 md:mb-12">
      <span className={`text-sm font-medium transition-colors ${!isYearly ? 'text-foreground' : 'text-text-secondary'}`}>
        {monthlyLabel}
      </span>
      <button
        onClick={() => setIsYearly(!isYearly)}
        className={`relative w-14 h-7 rounded-full transition-colors ${
          isYearly ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            isYearly ? 'translate-x-7' : ''
          }`}
        />
      </button>
      <span className={`text-sm font-medium transition-colors ${isYearly ? 'text-foreground' : 'text-text-secondary'}`}>
        {yearlyLabel}
      </span>
      {yearlyDiscount && (
        <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
          {yearlyDiscount}
        </span>
      )}
    </div>
  );

  const renderCardsStyle = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {plans.map((plan, index) => (
        <div
          key={plan.id || index}
          className={`relative rounded-2xl p-6 md:p-8 transition-all ${
            plan.highlighted
              ? 'bg-primary text-primary-foreground shadow-xl scale-105 z-10'
              : 'bg-card border border-border hover:border-primary/50 hover:shadow-lg'
          }`}
        >
          {plan.highlighted && (
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary-foreground text-primary text-xs font-semibold rounded-full">
              Популярный
            </div>
          )}

          <h3 className="text-xl md:text-2xl font-bold mb-2">{plan.name}</h3>
          {plan.description && (
            <p className={`text-sm mb-4 ${plan.highlighted ? 'opacity-90' : 'text-text-secondary'}`}>
              {plan.description}
            </p>
          )}

          <div className="mb-6">
            <div>
              <span className="text-3xl md:text-4xl font-bold">{getPrice(plan)}</span>
              <span className={`ml-1 ${plan.highlighted ? 'opacity-80' : 'text-text-secondary'}`}>
                {currency} / {getPeriod(plan)}
              </span>
            </div>
            {getBillingNote(plan) && (
              <p className={`text-xs mt-1 ${plan.highlighted ? 'opacity-70' : 'text-text-secondary'}`}>
                {getBillingNote(plan)}
              </p>
            )}
          </div>

          <ul className="space-y-3 mb-6">
            {(plan.features || []).map((feature, fIndex) => (
              <li key={fIndex} className="flex items-start gap-2 text-sm">
                <Icon
                  name="Check"
                  size={18}
                  className={`flex-shrink-0 mt-0.5 ${plan.highlighted ? '' : 'text-primary'}`}
                />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <Button
            variant={plan.highlighted ? 'secondary' : 'default'}
            fullWidth
            href={plan.buttonLink}
          >
            {plan.buttonText || 'Выбрать'}
          </Button>
        </div>
      ))}
    </div>
  );

  const renderCompactStyle = () => (
    <div className="max-w-3xl mx-auto space-y-4">
      {plans.map((plan, index) => (
        <div
          key={plan.id || index}
          className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 md:p-6 rounded-xl border transition-colors ${
            plan.highlighted
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          }`}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold">{plan.name}</h3>
              {plan.highlighted && (
                <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                  Популярный
                </span>
              )}
            </div>
            {plan.description && (
              <p className="text-sm text-text-secondary">{plan.description}</p>
            )}
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-right">
              <div>
                <span className="text-2xl font-bold">{getPrice(plan)}</span>
                <span className="text-text-secondary ml-1">
                  {currency} / {getPeriod(plan)}
                </span>
              </div>
              {getBillingNote(plan) && (
                <p className="text-xs text-text-secondary">{getBillingNote(plan)}</p>
              )}
            </div>
            <Button variant={plan.highlighted ? 'default' : 'outline'} size="sm" href={plan.buttonLink}>
              {plan.buttonText || 'Выбрать'}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderHighlightedStyle = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-0">
      {plans.map((plan, index) => (
        <div
          key={plan.id || index}
          className={`p-6 md:p-8 ${
            plan.highlighted
              ? 'bg-primary text-primary-foreground rounded-2xl lg:rounded-none lg:rounded-t-2xl lg:rounded-b-2xl shadow-xl lg:-my-4 z-10'
              : 'bg-card border border-border first:rounded-2xl first:lg:rounded-l-2xl first:lg:rounded-r-none last:rounded-2xl last:lg:rounded-r-2xl last:lg:rounded-l-none'
          }`}
        >
          <h3 className="text-lg md:text-xl font-bold mb-1">{plan.name}</h3>
          {plan.description && (
            <p className={`text-sm mb-4 ${plan.highlighted ? 'opacity-80' : 'text-text-secondary'}`}>
              {plan.description}
            </p>
          )}

          <div className="mb-4">
            <div>
              <span className="text-2xl md:text-3xl font-bold">{getPrice(plan)}</span>
              <span className={`text-sm ml-1 ${plan.highlighted ? 'opacity-70' : 'text-text-secondary'}`}>
                {currency} / {getPeriod(plan)}
              </span>
            </div>
            {getBillingNote(plan) && (
              <p className={`text-xs mt-1 ${plan.highlighted ? 'opacity-60' : 'text-text-secondary'}`}>
                {getBillingNote(plan)}
              </p>
            )}
          </div>

          <ul className="space-y-2 mb-6">
            {(plan.features || []).map((feature, fIndex) => (
              <li key={fIndex} className="flex items-start gap-2 text-sm">
                <Icon
                  name="Check"
                  size={16}
                  className={`flex-shrink-0 mt-0.5 ${plan.highlighted ? '' : 'text-primary'}`}
                />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <Button
            variant={plan.highlighted ? 'secondary' : 'outline'}
            fullWidth
            size="sm"
            href={plan.buttonLink}
          >
            {plan.buttonText || 'Выбрать'}
          </Button>
        </div>
      ))}
    </div>
  );

  return (
    <section
      style={{
        paddingTop: styles?.paddingTop || 64,
        paddingBottom: styles?.paddingBottom || 64,
        backgroundColor: styles?.backgroundColor || '#FFFFFF'
      }}
    >
      <Container>
        {/* Заголовок */}
        <Text
          as="h2"
          data={typeof data?.title === 'object' ? data.title : { text: data?.title }}
          defaults={{ size: '3xl', weight: 'bold', color: 'foreground' }}
          align="center"
          className={data?.subtitle ? 'mb-2 md:mb-3' : 'mb-8 md:mb-12'}
        />

        {/* Подзаголовок */}
        <Text
          as="p"
          data={typeof data?.subtitle === 'object' ? data.subtitle : { text: data?.subtitle }}
          defaults={{ size: 'lg', weight: 'normal', color: 'text-secondary' }}
          align="center"
          className={showPeriodToggle ? 'mb-6' : 'mb-8 md:mb-12'}
        />

        {/* Переключатель месяц/год */}
        {showPeriodToggle && <PeriodToggle />}

        {plans.length > 0 ? (
          <>
            {sectionStyle === 'cards' && renderCardsStyle()}
            {sectionStyle === 'compact' && renderCompactStyle()}
            {sectionStyle === 'highlighted' && renderHighlightedStyle()}
          </>
        ) : (
          <div className="text-center py-12 text-text-secondary">
            <Icon name="CreditCard" size="3xl" className="mx-auto mb-4 opacity-50" />
            <p>Добавьте тарифы в настройках</p>
          </div>
        )}
      </Container>
    </section>
  );
}
